import React, { JSX } from 'react';

import { Box, Button, ButtonProps, DialogProps, IconButton, Stack, SwipeableDrawer, Typography } from '@mui/material';

import ChevronLeft from '@/components/SvgIcons/ChevronLeft';
import Close from '@/components/SvgIcons/Close';
import colors from '@/styles/themes/colors';

import styles from './SwipeableModal.module.scss';

interface SwipeableModalProps extends DialogProps {
  title: string | undefined;
  onOpen: () => void;
  onClose: () => void;
  description?: string;
  onConfirm?: () => void;
  confirmBtnText?: string;
  ConfirmBtnProps?: ButtonProps;
  hideConfirmButton?: boolean;
  onCancel?: () => void;
  cancelBtnText?: string;
  CancelBtnProps?: ButtonProps;
  hideCancelButton?: boolean;
  titleActions?: JSX.Element | null;
  icon?: React.ReactNode;
  customButton?: React.ReactNode;
  scrollRef?: React.Ref<HTMLDivElement>;
  arrowBack?: boolean;
  onBack?: () => void;
  removePadding?: boolean;
  noTitle?: boolean;
  zIndex?: number;
}

const SwipeableModal = ({
  open,
  title,
  onOpen,
  onClose,
  description,
  onConfirm,
  confirmBtnText = 'Potvrdi',
  ConfirmBtnProps,
  hideConfirmButton = false,
  onCancel,
  cancelBtnText = 'Odustani',
  CancelBtnProps,
  hideCancelButton = false,
  titleActions,
  customButton,
  arrowBack,
  onBack,
  removePadding,
  noTitle,
  children,
  zIndex = 1300,
}: SwipeableModalProps) => (
  <SwipeableDrawer
    anchor="bottom"
    open={open}
    onClose={onClose}
    onOpen={onOpen}
    keepMounted
    title={title}
    disableScrollLock={false}
    sx={{
      '&.MuiDrawer-root': {
        zIndex,
      },
      '.MuiDrawer-paper ': {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        maxHeight: '80dvh',
        overflowY: 'hidden',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
      },
    }}
  >
    <Box className={styles.puller} />
    <Stack
      component="div"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={styles.swipeableContainer}
      sx={{ flexShrink: 0 }}
      position={noTitle ? 'absolute' : 'relative'}
      top={noTitle ? 10 : 'auto'}
      right={noTitle ? 0 : 'auto'}
      zIndex={noTitle ? 1300 : 'auto'}
    >
      {arrowBack && (
        <IconButton size="large" onClick={onBack} sx={{ color: colors.graytone400 }}>
          <ChevronLeft />
        </IconButton>
      )}
      <Stack pl={1.5}>
        <Typography variant="subtitle1" component="p" fontWeight={500} color={colors.graytone950}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body1" color={colors.blue500}>
            {description}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={3}>
        {titleActions && <Box flex="none">{titleActions}</Box>}
        <IconButton size="large" onClick={onClose} sx={{ color: noTitle ? colors.white : colors.graytone400 }}>
          <Close />
        </IconButton>
      </Stack>
    </Stack>
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: 3,
        minHeight: 0,
        borderBottom: !hideCancelButton || !hideConfirmButton ? `1px solid ${colors.graytone200}` : undefined,

        '@media (max-width: 768px)': {
          paddingInline: removePadding ? 0 : 1.5,
          paddingTop: 0,
          paddingBottom: removePadding ? 0 : 3,
        },
      }}
    >
      {children}
    </Box>
    {(!hideCancelButton || !hideConfirmButton) && (
      <Stack direction="column" spacing={2} className={styles.swipeableContainer} sx={{ flexShrink: 0 }}>
        {customButton ? (
          <Box sx={{ width: '100%' }}>{customButton}</Box>
        ) : (
          !hideConfirmButton && (
            <Button onClick={onConfirm} fullWidth size="large" {...ConfirmBtnProps}>
              {confirmBtnText}
            </Button>
          )
        )}
        {!hideCancelButton && (
          <Button onClick={onCancel} size="large" variant="outlined" fullWidth {...CancelBtnProps}>
            {cancelBtnText}
          </Button>
        )}
      </Stack>
    )}
  </SwipeableDrawer>
);

export default SwipeableModal;
